<template>
  <BottomSheetModal
    v-model="isOpen"
    hide-confirm
    @close="handleClose"
  >
    <template #title>
      <div class="modal-title-container">
        <span>{{ t('teamMembers') }}</span>
        <span v-if="teamName" class="modal-subtitle">{{ teamName }}</span>
      </div>
    </template>

    <div class="members-content">
      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <v-progress-circular indeterminate size="32" />
      </div>

      <template v-else>
        <RoleSection
          v-for="roleCfg in visibleRoles"
          :key="roleCfg.key"
          :users="roleData[roleCfg.key]"
          :title="t(roleCfg.titleKey)"
          :add-label="t(roleCfg.addLabelKey)"
          :empty-label="t(roleCfg.emptyLabelKey)"
          :editable="canManage"
          show-count
          :badges="getMemberBadges"
          @add="openInvite(roleCfg.key)"
          @remove="(index, user) => removeMember(user._id, roleCfg.key)"
          @click-user="openPersonDetail"
        />
      </template>
    </div>

    <!-- Invite Modal -->
    <InvitePersonsModal
      v-model="showInviteModal"
      :title="inviteTitle"
      :confirm-button-label="t('invite.add')"
      multiple
      :already-selected="allMembers"
      :entity-id="teamId"
      entity-type="team"
      :role="inviteRole"
      @done="handleInviteDone"
    />

    <PlayerCardModal ref="playerCardRef" />
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import InvitePersonsModal from '@/components/InvitePersonsModal.vue'
import RoleSection from '@/components/Team/RoleSection.vue';
import PlayerCardModal from '@/components/PlayerCardModal.vue';
import { useUserStore } from '@/stores/user/userStore'
import axiosInstance from '@/services/axiosInstance'
import { TEAM_ROLES, mapUserLite } from './teamRoles'
import type { UserLite, TeamRoleKey, TeamRoleConfig } from './teamRoles'
import type { RoleBadge } from './RoleSection.vue'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps<{
  modelValue: boolean
  teamId: string
  readOnly?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'updated'])

const userStore = useUserStore()

// State
const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const loading = ref(false)
const teamName = ref('')

/** Which roles to display in the members modal */
const visibleRoles: TeamRoleConfig[] = [
  TEAM_ROLES.players,
  TEAM_ROLES.goalkeepers,
  TEAM_ROLES.coaches,
]

/** Reactive storage for all role arrays (populated from API) */
const roleData = reactive<Record<TeamRoleKey, UserLite[]>>({
  admins: [],
  coaches: [],
  players: [],
  goalkeepers: [],
  externalOrganizers: [],
  treasurers: [],
  captains: [],
  timekeepers: [],
})

/** IDs for badge lookups (admin/coach/treasurer roles) */
const badgeLookup = reactive<Record<string, string[]>>({
  admins: [],
  coaches: [],
  treasurers: [],
})

// Invite modal
const showInviteModal = ref(false)
const inviteRole = ref<TeamRoleKey>('players')
const playerCardRef = ref<InstanceType<typeof PlayerCardModal>>()

const inviteTitle = computed(() => {
  const cfg = TEAM_ROLES[inviteRole.value]
  return cfg ? t(cfg.addLabelKey) : t('invitePeople')
})

// Permissions
const canManage = computed(() => {
  if (props.readOnly) return false

  const userId = userStore.user?._id
  const roles = userStore.user?.userRoles || []

  if (roles.includes('administrator')) return true
  if (roles.includes('coach')) return true
  if (!userId) return false

  return badgeLookup.admins.includes(userId) || badgeLookup.coaches.includes(userId)
})

// Members already in the target role (exclude only same-role duplicates)
const allMembers = computed(() => {
  return roleData[inviteRole.value] || []
})

// Methods
const getMemberBadges = (userId: string): RoleBadge[] => {
  const badges: RoleBadge[] = []
  if (badgeLookup.admins.includes(userId)) badges.push({ label: t('admin'), color: TEAM_ROLES.admins.badgeColor })
  if (badgeLookup.coaches.includes(userId)) badges.push({ label: t('coach'), color: TEAM_ROLES.coaches.badgeColor })
  if (badgeLookup.treasurers.includes(userId)) badges.push({ label: t('treasurer'), color: TEAM_ROLES.treasurers.badgeColor })
  return badges
}

const loadTeamMembers = async () => {
  if (!props.teamId) return

  loading.value = true
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/teams/${props.teamId}`)

    teamName.value = data.name || ''

    // Populate badge lookups
    badgeLookup.admins = (data.admins || []).map((a: any) => typeof a === 'string' ? a : a._id)
    badgeLookup.coaches = (data.coaches || []).map((c: any) => typeof c === 'string' ? c : c._id)
    badgeLookup.treasurers = (data.treasurers || []).map((t: any) => typeof t === 'string' ? t : t._id)

    // Populate role arrays using mapUserLite
    for (const roleCfg of visibleRoles) {
      const raw = data[roleCfg.key]
      roleData[roleCfg.key] = Array.isArray(raw) ? raw.map(mapUserLite) : []
    }
  } catch (error) {
    console.error('Error loading team members:', error)
    for (const roleCfg of visibleRoles) {
      roleData[roleCfg.key] = []
    }
  } finally {
    loading.value = false
  }
}

const openPersonDetail = (person: UserLite) => {
  playerCardRef.value?.open(person)
}

const openInvite = (roleKey: TeamRoleKey) => {
  inviteRole.value = roleKey
  showInviteModal.value = true
}

const handleInviteDone = async (users: UserLite[]) => {
  showInviteModal.value = false

  if (!users.length) return

  try {
    const field = inviteRole.value

    // Use the role-specific POST endpoint for each user
    for (const user of users) {
      await axiosInstance.post(`${API_BASE_URL}/teams/${props.teamId}/${field}`, {
        userId: user._id
      })
    }

    await loadTeamMembers()
    emit('updated')
  } catch (error) {
    console.error('Error adding members:', error)
  }
}

const removeMember = async (userId: string, field: TeamRoleKey) => {
  try {
    await axiosInstance.delete(`${API_BASE_URL}/teams/${props.teamId}/${field}`, {
      data: { userId }
    })

    roleData[field] = roleData[field].filter(u => u._id !== userId)
    emit('updated')
  } catch (error) {
    console.error('Error removing member:', error)
  }
}

const handleClose = () => {
  isOpen.value = false
}

// Watch for modal open
watch(isOpen, async (val) => {
  if (val && props.teamId) {
    await loadTeamMembers()
  }
})
</script>

<style lang="scss" scoped>
.modal-title-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.modal-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.5);
  margin-top: 2px;
}

.members-content {
  padding-bottom: 20px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
}

.text-disabled {
  color: rgba(var(--v-theme-sheetText), 0.3);
}
</style>
